import React from "react";
import {
  Typography,
  Box,
  Container,
  Grid,
  Paper,
  Avatar,
  Button,
  Divider,
} from "@mui/material";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import Diversity3Icon from "@mui/icons-material/Diversity3";
import Navbar from "../../../Components/Navbar";
import Footer from "../../../Components/Footer";
import Colorapps from "../../../configcss/color";

const healthcareFeatures = [
  {
    icon: <HealthAndSafetyIcon fontSize="large" />,
    title: "Preventive Health Checkups",
    description:
      "We offer free and affordable health checkups to detect and prevent diseases early.",
  },
  {
    icon: <LocalHospitalIcon fontSize="large" />,
    title: "Accessible Clinics",
    description:
      "Our clinics are open to all, offering basic medical services and consultations.",
  },
  {
    icon: <Diversity3Icon fontSize="large" />,
    title: "Community Health Drives",
    description:
      "Organizing health camps in underserved areas for awareness, vaccination, and care.",
  },
];

function HealthCare() {
  return (
    <>
      <Navbar />
      <Box sx={{ backgroundColor: Colorapps.textprimary }}>
        {/* Hero Section */}
        <Box
          sx={{
            position: "relative",
            height: { xs: "50vh", md: "70vh" },
            background: `url("https://images.unsplash.com/photo-1588776814546-ec7e53b2c2e5") center/cover no-repeat`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              width: "100%",
              height: "100%",
              background: "rgba(0,0,0,0.6)",
            }}
          />
          <Box
            sx={{
              position: "relative",
              color: "#fff",
              textAlign: "center",
              px: 3,
              zIndex: 1,
            }}
          >
            <Typography variant="h3" fontWeight="bold">
              Health Care For All
            </Typography>
            <Typography variant="body1" mt={2}>
              Bringing essential medical support to every corner of society.
            </Typography>
            <Button
              variant="contained"
              href="#healthcare-program"
              sx={{
                mt: 4,
                px: 4,
                borderRadius: 20,
                backgroundColor: Colorapps.themeaccent,
                "&:hover": {
                  backgroundColor: Colorapps.themesecondary,
                },
              }}
            >
              Learn More
            </Button>
          </Box>
        </Box>

        {/* Mission Section */}
        <Box py={10} sx={{ backgroundColor: Colorapps.backgroundlight }}>
          <Container maxWidth="md">
            <Typography variant="h4" textAlign="center" fontWeight="bold" mb={4}>
              Our Mission
            </Typography>
            <Typography textAlign="center" color="text.secondary">
              We aim to make healthcare accessible to everyone, regardless of income or location. Our programs ensure that no one is left behind when it comes to basic medical support, awareness, and education.
            </Typography>
          </Container>
        </Box>

        {/* Features Section */}
        <Box
          py={10}
          id="healthcare-program"
          sx={{
            backgroundColor: Colorapps.textprimary,
            borderTop: `1px solid ${Colorapps.themesecondary}`,
          }}
        >
          <Container maxWidth="lg">
            <Typography
              variant="h4"
              textAlign="center"
              fontWeight="bold"
              color={Colorapps.textsecondary}
              mb={6}
            >
              Our Services
            </Typography>
            <Grid container spacing={4}>
              {healthcareFeatures.map((feature, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Paper
                    elevation={4}
                    sx={{
                      padding: 4,
                      textAlign: "center",
                      borderRadius: "16px",
                      backgroundColor: "#fff",
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: Colorapps.themeaccent,
                        mb: 2,
                        width: 56,
                        height: 56,
                        mx: "auto",
                      }}
                    >
                      {feature.icon}
                    </Avatar>
                    <Typography variant="h6" fontWeight="bold">
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" mt={1}>
                      {feature.description}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Stats Section */}
        <Box py={10} sx={{ backgroundColor: "#f7f7f7" }}>
          <Container>
            <Grid container spacing={4} justifyContent="center">
              {[
                { label: "Clinics Established", value: "120+" },
                { label: "Volunteers", value: "800+" },
                { label: "People Reached", value: "50,000+" },
              ].map((stat, i) => (
                <Grid item xs={12} sm={4} key={i}>
                  <Box textAlign="center">
                    <Typography variant="h3" fontWeight="bold" color="primary">
                      {stat.value}
                    </Typography>
                    <Typography color="text.secondary">{stat.label}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Testimonials Section */}
        <Box py={10} sx={{ backgroundColor: Colorapps.textprimary }}>
          <Container>
            <Typography
              variant="h4"
              textAlign="center"
              fontWeight="bold"
              color={Colorapps.textsecondary}
              mb={6}
            >
              What People Say
            </Typography>
            <Grid container spacing={4}>
              {[
                {
                  name: "Amina B.",
                  comment: "Their free health checkup saved my father's life. Forever grateful.",
                },
                {
                  name: "Rizwan K.",
                  comment: "I volunteered last year—what an amazing team and impact!",
                },
              ].map((testimonial, i) => (
                <Grid item xs={12} md={6} key={i}>
                  <Paper
                    elevation={2}
                    sx={{
                      p: 4,
                      borderLeft: `5px solid ${Colorapps.themeaccent}`,
                      backgroundColor: "#fff",
                    }}
                  >
                    <Typography variant="body1" fontStyle="italic" mb={1}>
                      “{testimonial.comment}”
                    </Typography>
                    <Typography variant="subtitle2" fontWeight="bold">
                      — {testimonial.name}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Call to Action */}
        <Box textAlign="center" py={10} sx={{ backgroundColor: Colorapps.themeaccent }}>
          <Typography variant="h4" fontWeight="bold" color="#fff" mb={2}>
            Ready to Make a Difference?
          </Typography>
          <Button
            variant="contained"
            color="inherit"
            sx={{
              fontWeight: "bold",
              px: 5,
              py: 1.5,
              borderRadius: 30,
              "&:hover": {
                backgroundColor: Colorapps.textsecondary,
                color: "#fff",
              },
            }}
          >
            Become a Volunteer
          </Button>
        </Box>
      </Box>
      <Footer />
    </>
  );
}

export default HealthCare;
